
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";

interface TripDetailsStepProps {
  duration: number;
  capacity: number;
  to: string;
  from: string;
  startTime: string;
  price: number;
  name: string;
  setName: (value: string) => void;
  phoneNumber: string;
  handlePhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  promoCode: string;
  setPromoCode: (value: string) => void;
  smsAgreement: boolean;
  setSmsAgreement: (value: boolean) => void;
  onTextMePaymentLink: () => void;
  onEditTripDetails: () => void;
  isSubmitting: boolean;
  isRoundTrip?: boolean;
  returnFrom?: string;
  returnTo?: string;
  returnStartTime?: string;
  endTime?: string;
  returnEndTime?: string;
  calculateEndTime?: (startTime: string, duration: number) => string;
  tripDate?: string;
  timezoneAbbreviation?: string;
}

export const TripDetailsStep = ({
  duration,
  capacity,
  to,
  from,
  startTime,
  price,
  name,
  setName,
  phoneNumber,
  handlePhoneNumberChange,
  promoCode,
  setPromoCode,
  smsAgreement,
  setSmsAgreement,
  onTextMePaymentLink,
  onEditTripDetails,
  isSubmitting,
  isRoundTrip = false,
  returnFrom = "",
  returnTo = "",
  returnStartTime = "",
  endTime = "",
  returnEndTime = "",
  calculateEndTime = () => "",
  tripDate = "",
  timezoneAbbreviation
}: TripDetailsStepProps) => {
  const formatTimeFor12Hour = (time24: string): string => {
    if (!time24) return "";
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `at ${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
    } catch (error) {
      return time24;
    }
  };

  // Format address to remove state and country
  const formatAddress = (address: string): string => {
    if (!address) return "";
    
    // Split by comma and take only the first 3 parts (venue, street, city)
    const parts = address.split(',').map(part => part.trim());
    const relevantParts = parts.slice(0, 3);
    
    return relevantParts.join(', ');
  };

  const canSubmit = name.trim() !== "" && phoneNumber.trim() !== "" && smsAgreement;

  // Calculate drop off time for display
  const getDropOffTime = () => {
    if (isRoundTrip) {
      return returnEndTime ? formatTimeFor12Hour(returnEndTime) : "";
    } else {
      return endTime ? formatTimeFor12Hour(endTime) : formatTimeFor12Hour(calculateEndTime(startTime, duration));
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-1 p-6">
        <h1 className="text-2xl font-bold text-center text-black mb-8">TRIP DETAILS</h1>
        
        <div className="bg-gray-50 rounded-2xl px-2 py-6 space-y-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Total Time Reserved</span>
            <span className="text-black font-semibold">{duration} Hours</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Bus Capacity</span>
            <span className="text-black font-semibold">{capacity} People</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Destination</span>
            <span className="text-black font-semibold text-right">{formatAddress(to)}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Pickup Address</span>
            <span className="text-black font-semibold text-right">{formatAddress(from)}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Trip Date</span>
            <span className="text-black font-semibold">{tripDate}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Pickup Time</span>
            <span className="text-black font-semibold">{formatTimeFor12Hour(startTime)}{timezoneAbbreviation ? ` ${timezoneAbbreviation}` : ''}</span>
          </div>

          {/* Show Special Instructions for round trips */}
          {isRoundTrip && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Special Instructions</span>
                <span className="text-black font-semibold">Drop off back at Pickup Address</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium"></span>
                <span className="text-black font-semibold">{getDropOffTime()}</span>
              </div>
            </>
          )}
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Trip Type</span>
            <span className="text-black font-semibold">{isRoundTrip ? "Round Trip" : "One Way"}</span>
          </div>
          
          <div className="flex justify-center pt-4">
            <button
              onClick={onEditTripDetails}
              className="text-black font-medium underline hover:text-gray-700"
            >
              Edit Trip Details
            </button>
          </div>
        </div>
        
        <div className="text-center mb-6">
          <div className="text-gray-600 text-lg mb-2">Total Price</div>
          <div className="text-4xl font-bold text-black">${price.toFixed(2)}</div>
        </div>
      </div>
      
      <div className="p-6">
        <p className="text-sm text-gray-500 mb-3">
          <span className="text-red-500">*</span> Required fields
        </p>
        <div className="bg-gradient-to-r from-green-400 to-cyan-400 rounded-2xl p-6 space-y-4">
          <Input
            placeholder="Full Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`h-12 border-0 text-black placeholder:text-gray-600 rounded-xl focus:ring-2 focus:ring-white ${name.trim() ? 'bg-gray-200' : 'bg-white'}`}
          />
          
          <Input
            type="tel"
            placeholder="Enter Your Phone Number *"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            className={`h-12 border-0 text-black placeholder:text-gray-600 rounded-xl focus:ring-2 focus:ring-white ${phoneNumber.trim() ? 'bg-gray-200' : 'bg-white'}`}
          />
          
          <Input
            placeholder="Enter Referral Code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            className="h-12 bg-white border-0 text-black placeholder:text-gray-600 rounded-xl focus:ring-2 focus:ring-white"
          />
          
          <Button
            onClick={onTextMePaymentLink}
            disabled={!canSubmit || isSubmitting}
            className="w-full bg-white text-black py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Text Me Payment Link'}
          </Button>
          
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="sms-agreement"
              checked={smsAgreement}
              onCheckedChange={(checked) => setSmsAgreement(checked === true)}
              className="mt-1 border-white data-[state=checked]:bg-white data-[state=checked]:text-black"
            />
            <label htmlFor="sms-agreement" className="text-sm text-white leading-relaxed cursor-pointer">
              I agree to receive SMS messages from Disco regarding party bus trips and pricing. I understand that I may opt out at any time by replying STOP to any text message I receive from Disco. Frequency may vary. Msg & data rates may apply. To view our privacy policy, <a href="https://www.discoveryconciergeservices.com/privacy-policy" className="underline hover:text-gray-200" target="_blank" rel="noopener noreferrer">click here</a>.
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
